import { Button, Modal, useSnackbar } from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { History } from 'lucide-react';
import { useState } from 'react';
import {
  clientsApi,
  getApiError,
  type Client,
  type ClientConsent,
  type ClientConsentKind,
} from '../../lib/api.js';
import { formatDate, formatDateTime } from '../../lib/time-zone.js';
import { RequestAlert } from '../../app/data-view.js';

/// As autorizações, na ordem em que a tela e o papel as mostram (ADR 0040, decisão 6).
const consentCopy: ReadonlyArray<{
  kind: ClientConsentKind;
  label: string;
  hint: string;
}> = [
  {
    kind: 'WHATSAPP_REMINDERS',
    label: 'Lembretes e confirmações pelo WhatsApp',
    hint: 'Aviso de horário marcado e pedido de confirmação.',
  },
  {
    kind: 'MARKETING',
    label: 'Novidades e promoções',
    hint: 'WhatsApp e e-mail. Separado dos lembretes: o cliente pode querer um e não o outro.',
  },
  {
    kind: 'IMAGE_USE',
    label: 'Uso de imagem',
    hint: 'Fotos de antes e depois no portfólio e nas redes sociais da empresa.',
  },
];

/// As autorizações moram no cadastro, não na ficha: a chave grava na hora, com quem marcou e quando,
/// e cada mudança fica no histórico. Sem permissão de editar, o bloco só se lê.
export function ClientConsents({
  client,
  canUpdate,
  timeZone,
}: {
  client: Client;
  canUpdate: boolean;
  timeZone: string;
}) {
  const queryClient = useQueryClient();
  const snackbar = useSnackbar();
  const [saving, setSaving] = useState<ClientConsentKind>();
  const [requestError, setRequestError] = useState<string>();
  const [historyOpen, setHistoryOpen] = useState(false);
  const [current, setCurrent] = useState(client.consents);
  const [source, setSource] = useState(client);
  /// A ficha salva de novo chega com as autorizações lidas do servidor; o que a chave gravou aqui
  /// vale até lá.
  if (source !== client) {
    setSource(client);
    setCurrent(client.consents);
  }

  async function toggle(kind: ClientConsentKind, granted: boolean) {
    setSaving(kind);
    setRequestError(undefined);
    try {
      const saved = await clientsApi.setConsent(client.id, kind, granted);
      setCurrent(saved.consents);
      await queryClient.invalidateQueries({ queryKey: ['company', 'clients'] });
      snackbar.success(granted ? 'Autorização marcada.' : 'Autorização retirada.');
    } catch (cause) {
      setRequestError(getApiError(cause));
    } finally {
      setSaving(undefined);
    }
  }

  return (
    <section className="mt-7 border-t border-surface-border pt-5">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h3 className="text-sm font-semibold">Autorizações</h3>
          <p className="mt-1 text-xs text-content-muted">
            O PDF da ficha e do prontuário imprime o que está marcado aqui. Cada mudança fica no
            histórico, com quem marcou e quando.
          </p>
        </div>
        <Button variant="outline" className="self-start" onClick={() => setHistoryOpen(true)}>
          <History className="size-4" />
          Histórico
        </Button>
      </div>
      <ul className="mt-4 divide-y divide-surface-border rounded-xl border border-surface-border">
        {consentCopy.map(({ kind, label, hint }) => {
          const state = current.find((consent) => consent.kind === kind);
          const granted = state?.granted ?? false;
          return (
            <li key={kind} className="flex items-center gap-4 p-3">
              <div className="min-w-0 flex-1">
                <strong className="block text-sm">{label}</strong>
                <span className="mt-0.5 block text-xs text-content-muted">
                  {stateText(state, timeZone) ?? hint}
                </span>
              </div>
              <ConsentSwitch
                label={label}
                checked={granted}
                disabled={!canUpdate || saving !== undefined}
                onChange={(next) => void toggle(kind, next)}
              />
            </li>
          );
        })}
      </ul>
      {requestError ? <RequestAlert message={requestError} /> : null}
      <ConsentHistory
        open={historyOpen}
        onOpenChange={setHistoryOpen}
        clientId={client.id}
        timeZone={timeZone}
      />
    </section>
  );
}

function stateText(state: ClientConsent | undefined, timeZone: string): string | null {
  if (!state?.changedAt) return null;
  const when = formatDate(state.changedAt, timeZone);
  const who = state.changedBy ? `, por ${state.changedBy}` : '';
  return state.granted ? `Autorizado em ${when}${who}.` : `Retirada em ${when}${who}.`;
}

/// Liga e desliga, com o nome da autorização para o leitor de tela.
function ConsentSwitch({
  label,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative h-6 w-11 shrink-0 rounded-full transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50 disabled:cursor-not-allowed disabled:opacity-60 ${
        checked ? 'bg-brand-500' : 'bg-content/15'
      }`}
    >
      <span
        aria-hidden="true"
        className={`absolute top-1 size-4 rounded-full bg-surface-card transition-[left] ${
          checked ? 'left-6' : 'left-1'
        }`}
      />
    </button>
  );
}

function ConsentHistory({
  open,
  onOpenChange,
  clientId,
  timeZone,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clientId: string;
  timeZone: string;
}) {
  const history = useQuery({
    queryKey: ['company', 'clients', clientId, 'consents'],
    queryFn: () => clientsApi.consentHistory(clientId),
    enabled: open,
    retry: false,
  });
  const labels = Object.fromEntries(consentCopy.map((item) => [item.kind, item.label]));
  return (
    <Modal
      open={open}
      onOpenChange={onOpenChange}
      title="Histórico de autorizações"
      description="Cada marcação e retirada, a mais recente primeiro. O histórico não se edita."
    >
      {history.isError ? (
        <RequestAlert message={getApiError(history.error)} />
      ) : history.isLoading ? (
        <p className="text-sm text-content-muted">Carregando histórico…</p>
      ) : history.data?.length ? (
        <ol className="space-y-2">
          {history.data.map((event) => (
            <li key={event.id} className="rounded-xl border border-surface-border p-3 text-sm">
              <strong className="block">
                {event.granted ? 'Marcada' : 'Retirada'}: {labels[event.kind]}
              </strong>
              <span className="text-xs text-content-muted">
                {formatDateTime(event.occurredAt, timeZone)} · {event.actorDisplayName}
              </span>
            </li>
          ))}
        </ol>
      ) : (
        <p className="rounded-xl border border-dashed border-surface-border p-4 text-center text-xs text-content-muted">
          Nenhuma autorização marcada ainda.
        </p>
      )}
    </Modal>
  );
}
